import { crmLinkGeneration } from "../components/engagements/activityCenter/opporotunityLayer/crmLinks";
import { CRM_TYPES } from "../constants/crmConstants";
import {
  extractLeadId,
  extractOpportunityId,
  extractLinkOpportunityId,
  CRM_OPPORTUNITY_EXTRACTORS,
} from "./opportunityHelper";

const SALESFORCE_TYPE_MAP = {
  [CRM_TYPES.SALESFORCE]: "usAutoSales",
  [CRM_TYPES.DEALER_TEAM]: "dealerTeam",
};

const checkIsOpportunity = (opportunity, crmIntegrationType) => {
  if (!opportunity) return false;
  return Boolean(CRM_OPPORTUNITY_EXTRACTORS[crmIntegrationType]?.(opportunity) || opportunity?.id);
};

/**
 * Builds a CRM link and extracts all related CRM details for a given opportunity/lead.
 * This is the standalone (non-hook) version for use outside React components.
 * For the hook version, use `buildCrmLink` from `useCrm` hook (auto-injects crmIntegrationType & companyId).
 *
 * ── Required GraphQL fields per CRM type ──────────────────────────────────
 *
 * 1) `opportunity` object needs these fields on lead opportunities:
 *    DS  → dsOpportunityEvents { dsEventId, dsLeadId }
 *    SF  → sfLeadOpportunities { sfOpportunityId }
 *    DT  → dtLeadOpportunities { dtOpportunityId }
 *    VIN → vsLeadOpportunities { vsLeadId, vsContactId }
 *    EL  → elLeadOpportunities { elOpportunityId }
 *    MF  → mfLeadOpportunities { mfLeadId }
 *    TK  → tkLeadOpportunities { tkLeadId }
 *
 * 2) `crmLeadObj` (from lead.crmLeadObj) needs these extracted lead fields:
 *    DS  → dsExtractedLead { dsLeadId }
 *    SF  → sfExtractedLead { sfLeadId }
 *    DT  → dtExtractedLead { dtLeadId }
 *    VIN → vsExtractedLead { vsLeadId }
 *    EL  → elExtractedLead { elLeadId }
 *    MF  → mfExtractedLead { mfProspectId, mfCustomerId }
 *
 * 3) `selectedLead` only needed for EL (uses `selectedLead.fullName` as search query)
 *
 * Queries that already fetch these fields:
 *    - GET_PAGINATED_LEADS (query.js ~line 1347)
 *    - GET_LEAD (query.js ~line 1928)
 *    - GET_LEAD_OPPORTUNITIES (query.js ~line 9662)
 *    - GET_LEAD_DETAIL_CLICK (query.js ~line 8632) — used by EmployeeCallTable, callDetailView
 *
 * ──────────────────────────────────────────────────────────────────────────
 *
 * @param {Object} params
 * @param {Object} params.opportunity - The opportunity item (with CRM sub-arrays)
 * @param {Object} [params.crmLeadObj] - The lead's CRM object (contains extractedLead data for leadId)
 * @param {Object} [params.selectedLead] - The selected lead object (needed for EL: uses fullName)
 * @param {string} [params.dsLeadId] - Optional DS lead ID for matching specific dsOpportunityEvents
 * @param {boolean} [params.isLeadLink=false] - If true, generates a lead-page link instead of opportunity-page link
 * @param {string} params.crmIntegrationType - CRM integration type (DS, DT, SF, VIN, EL, MF)
 * @param {string|number} params.companyId - Company ID
 * @returns {Object} { crmLink, isOpportunity, leadId, opportunityId, linkOpprId, salesForceType, hasCrmId }
 */
export const buildCrmLinkUtil = ({
  opportunity,
  crmLeadObj,
  selectedLead,
  dsLeadId,
  isLeadLink = false,
  crmIntegrationType,
  companyId,
}) => {
  const salesForceType = SALESFORCE_TYPE_MAP[crmIntegrationType] || "";

  const leadId = extractLeadId(crmLeadObj, crmIntegrationType);
  const resolvedDsLeadId = dsLeadId || leadId;

  const opportunityId = extractOpportunityId(opportunity, crmIntegrationType, resolvedDsLeadId);
  const linkOpprId = extractLinkOpportunityId(
    opportunity,
    crmIntegrationType,
    selectedLead,
    resolvedDsLeadId
  );

  const isOpportunity = isLeadLink ? false : checkIsOpportunity(opportunity, crmIntegrationType);
  const hasCrmId = String(opportunityId) !== String(opportunity?.id);

  const crmLink = hasCrmId
    ? crmLinkGeneration(
        crmIntegrationType,
        isOpportunity,
        linkOpprId,
        leadId,
        salesForceType,
        companyId
      )
    : null;

  return {
    crmLink,
    isOpportunity,
    leadId,
    opportunityId,
    linkOpprId,
    salesForceType,
    hasCrmId,
  };
};
